// =============================================================================
// Platform User Detail Page — Read-only view of a single platform user
// =============================================================================

import { useParams, Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Spinner,
} from '@tkpod/ui';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PlatformUserOrganization {
  organizationId: string;
  organizationName: string;
  organizationSlug: string;
  role: string;
  permissions: string[];
  isActive: boolean;
}

interface PlatformUserAdminInfo {
  id: string;
  roleSlug: string;
  roleName: string;
  isActive: boolean;
}

interface PlatformUserDetail {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  avatar: string | null;
  phone: string | null;
  status: 'ACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION' | 'DELETED';
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  organizations: PlatformUserOrganization[];
  adminUser: PlatformUserAdminInfo | null;
}

interface PlatformUserDetailResponse {
  data: PlatformUserDetail;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<
  PlatformUserDetail['status'],
  { label: string; variant: 'success' | 'danger' | 'warning' | 'default' }
> = {
  ACTIVE: { label: 'Active', variant: 'success' },
  SUSPENDED: { label: 'Suspended', variant: 'danger' },
  PENDING_VERIFICATION: { label: 'Pending Verification', variant: 'warning' },
  DELETED: { label: 'Deleted', variant: 'default' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function displayName(user: PlatformUserDetail): string {
  const parts = [user.firstName, user.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : user.email;
}

function initials(user: PlatformUserDetail): string {
  const first = user.firstName?.[0] ?? '';
  const last = user.lastName?.[0] ?? '';
  if (first || last) return (first + last).toUpperCase();
  return user.email[0]?.toUpperCase() ?? '?';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PlatformUserDetailPage() {
  const { id } = useParams<{ id: string }>();

  // -- Query ----------------------------------------------------------------
  const userQuery = useQuery<PlatformUserDetailResponse>({
    queryKey: ['platform-users', id],
    queryFn: () =>
      api.get<PlatformUserDetailResponse>(
        `/api/v1/admin/platform-users/${id}`,
      ),
    enabled: !!id,
  });

  const user = userQuery.data?.data;

  // -- Loading state --------------------------------------------------------
  if (userQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" label="Loading user details..." />
      </div>
    );
  }

  // -- Error / not found state ----------------------------------------------
  if (userQuery.isError || !user) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <p className="text-sm text-red-400">
            {userQuery.isError
              ? `Failed to load user: ${userQuery.error?.message ?? 'Unknown error'}`
              : 'User not found.'}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void userQuery.refetch()}
            >
              Retry
            </Button>
            <Link to="/platform-users">
              <Button variant="ghost" size="sm">
                Back to Platform Users
              </Button>
            </Link>
          </div>
        </div>
      </Card>
    );
  }

  const statusCfg = STATUS_CONFIG[user.status];

  // -- Render ---------------------------------------------------------------
  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-text-tertiary">
        <Link
          to="/platform-users"
          className="hover:text-text-primary transition-colors"
        >
          Platform Users
        </Link>
        <span>/</span>
        <span className="text-text-primary">{displayName(user)}</span>
      </nav>

      {/* User profile card */}
      <Card>
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
          {/* Avatar placeholder */}
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-brand-600/20 text-xl font-bold text-brand-400">
            {initials(user)}
          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-xl font-semibold text-text-primary">
                {displayName(user)}
              </h2>
              <Badge variant={statusCfg.variant} size="sm" dot>
                {statusCfg.label}
              </Badge>
              {user.emailVerified ? (
                <Badge variant="success" size="sm">
                  Email Verified
                </Badge>
              ) : (
                <Badge variant="warning" size="sm">
                  Email Not Verified
                </Badge>
              )}
            </div>
            <p className="mt-1 text-sm text-text-secondary">{user.email}</p>

            {/* Metadata grid */}
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <MetadataItem label="Phone">
                <span className="text-sm text-text-primary">
                  {user.phone ?? '—'}
                </span>
              </MetadataItem>
              <MetadataItem label="2FA Enabled">
                {user.twoFactorEnabled ? (
                  <Badge variant="success" size="sm">
                    Enabled
                  </Badge>
                ) : (
                  <span className="text-sm text-text-tertiary">Disabled</span>
                )}
              </MetadataItem>
              <MetadataItem label="Last Login">
                <span className="text-sm text-text-primary">
                  {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}
                </span>
              </MetadataItem>
              <MetadataItem label="Created">
                <span className="text-sm text-text-primary">
                  {formatDate(user.createdAt)}
                </span>
              </MetadataItem>
            </div>
          </div>
        </div>
      </Card>

      {/* Organizations card */}
      <Card>
        <CardHeader
          title="Organizations"
          description={`This user belongs to ${user.organizations.length} organization${user.organizations.length !== 1 ? 's' : ''}.`}
        />

        {user.organizations.length === 0 ? (
          <div className="mt-4 flex items-center justify-center py-8 text-sm text-text-tertiary">
            This user does not belong to any organization.
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto rounded-lg border border-border">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Organization
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-surface">
                {user.organizations.map((org) => (
                  <tr
                    key={org.organizationId}
                    className="transition-colors hover:bg-surface-secondary"
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-text-primary">
                          {org.organizationName}
                        </p>
                        <p className="text-xs text-text-tertiary">
                          {org.organizationSlug}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge variant="default" size="sm">
                        {org.role}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {org.isActive ? (
                        <Badge variant="success" size="sm" dot>
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="danger" size="sm" dot>
                          Inactive
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Admin access card */}
      <Card>
        <CardHeader
          title="Admin Access"
          description={
            user.adminUser
              ? 'This user has administrative access to the admin panel.'
              : 'This user does not have admin access.'
          }
        />

        {user.adminUser ? (
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <MetadataItem label="Admin Role">
              <Badge
                variant="primary"
                size="sm"
              >
                {user.adminUser.roleName}
              </Badge>
            </MetadataItem>
            <MetadataItem label="Role Slug">
              <code className="rounded bg-surface-tertiary px-1.5 py-0.5 text-xs text-text-secondary">
                {user.adminUser.roleSlug}
              </code>
            </MetadataItem>
            <MetadataItem label="Admin Status">
              {user.adminUser.isActive ? (
                <Badge variant="success" size="sm" dot>
                  Active
                </Badge>
              ) : (
                <Badge variant="danger" size="sm" dot>
                  Suspended
                </Badge>
              )}
            </MetadataItem>
          </div>
        ) : (
          <div className="mt-4 flex items-center justify-center py-8 text-sm text-text-tertiary">
            No admin access.
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function MetadataItem({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium uppercase tracking-wider text-text-tertiary">
        {label}
      </p>
      <div>{children}</div>
    </div>
  );
}
